// src/pages/accounts-receivable-management-dashboard/components/ReceivableFilterBar.jsx
import React, { useEffect, useMemo, useState } from "react";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import Icon from "../../../components/AppIcon";
import { formatBaseLabel } from "../../../utils/baseDisplay";

function isoToday() {
  return new Date().toISOString().slice(0, 10);
}

function startOfMonthISO(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}-01`;
}

function addDaysISO(iso, days) {
  const dt = new Date(`${iso}T00:00:00`);
  dt.setDate(dt.getDate() + days);
  return dt.toISOString().slice(0, 10);
}

const normalizeValue = (next) => {
  const today = isoToday();
  const defaultDe = startOfMonthISO();
  const v = next || {};

  const base = String(v.base || "ALL").toUpperCase();
  const situacao = String(v.situacao || "TODOS").toUpperCase();

  const riscoRaw = v.risco == null ? "TODOS" : String(v.risco);
  const risco =
    riscoRaw === "TODOS"
      ? "TODOS"
      : riscoRaw.charAt(0).toUpperCase() + riscoRaw.slice(1).toLowerCase();

  const periodoPreset = String(v.periodoPreset || "MES").toUpperCase();

  const de = v.de || defaultDe;
  const ate = v.ate || today;

  return {
    base: base === "A" || base === "B" ? base : "ALL",
    de,
    ate,
    q: String(v.q || ""),
    situacao:
      situacao === "ABERTO" || situacao === "BAIXADO" || situacao === "CANCELADO"
        ? situacao
        : "TODOS",
    risco:
      risco === "Baixo" || risco === "Médio" || risco === "Alto" || risco === "Crítico"
        ? risco
        : "TODOS",
    periodoPreset:
      periodoPreset === "HOJE" ||
      periodoPreset === "7D" ||
      periodoPreset === "30D" ||
      periodoPreset === "MES" ||
      periodoPreset === "CUSTOM"
        ? periodoPreset
        : "MES",
  };
};

const isSame = (a, b) => {
  const x = normalizeValue(a);
  const y = normalizeValue(b);
  return (
    x.base === y.base &&
    x.de === y.de &&
    x.ate === y.ate &&
    x.q === y.q &&
    x.situacao === y.situacao &&
    x.risco === y.risco &&
    x.periodoPreset === y.periodoPreset
  );
};

const ReceivableFilterBar = ({ value, onChange, onRefresh, isLoading, onNewCharge }) => {
  const [local, setLocal] = useState(() => normalizeValue(value));

  // ✅ se o pai mudar (ex: reset, load, etc), sincroniza o estado local
  useEffect(() => {
    if (!isSame(local, value)) {
      setLocal(normalizeValue(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const baseOptions = [
    { value: "ALL", label: formatBaseLabel("ALL") },
    { value: "A", label: formatBaseLabel("A") },
    { value: "B", label: formatBaseLabel("B") },
  ];

  const periodoOptions = [
    { value: "MES", label: "Este Mês" },
    { value: "HOJE", label: "Hoje" },
    { value: "7D", label: "Últimos 7 dias" },
    { value: "30D", label: "Últimos 30 dias" },
    { value: "CUSTOM", label: "Personalizado" },
  ];

  const statusOptions = [
    { value: "TODOS", label: "Todos os Status" },
    { value: "ABERTO", label: "Em aberto" },
    { value: "BAIXADO", label: "Baixados" },
    { value: "CANCELADO", label: "Cancelados" },
  ];

  const riscoOptions = [
    { value: "TODOS", label: "Todos os Riscos" },
    { value: "Baixo", label: "Baixo" },
    { value: "Médio", label: "Médio" },
    { value: "Alto", label: "Alto" },
    { value: "Crítico", label: "Crítico" },
  ];

  const hasActive = useMemo(() => {
    const v = local;
    const defaultDe = startOfMonthISO();
    const defaultAte = isoToday();
    return (
      v.base !== "ALL" ||
      v.situacao !== "TODOS" ||
      v.risco !== "TODOS" ||
      (v.q || "").trim() !== "" ||
      v.de !== defaultDe ||
      v.ate !== defaultAte ||
      v.periodoPreset !== "MES"
    );
  }, [local]);

  const emit = (next) => {
    const normalized = normalizeValue(next);
    setLocal(normalized);
    onChange?.(normalized);
  };

  const setPreset = (presetRaw) => {
    const preset = String(presetRaw || "").toUpperCase();
    const today = isoToday();

    if (preset === "HOJE") {
      emit({ ...local, periodoPreset: "HOJE", de: today, ate: today });
      return;
    }
    if (preset === "7D") {
      emit({ ...local, periodoPreset: "7D", de: addDaysISO(today, -6), ate: today });
      return;
    }
    if (preset === "30D") {
      emit({ ...local, periodoPreset: "30D", de: addDaysISO(today, -29), ate: today });
      return;
    }
    if (preset === "MES") {
      emit({ ...local, periodoPreset: "MES", de: startOfMonthISO(), ate: today });
      return;
    }

    // CUSTOM: mantém datas atuais (não zera)
    emit({ ...local, periodoPreset: "CUSTOM" });
  };

  const clear = () => {
    emit({
      base: "ALL",
      de: startOfMonthISO(),
      ate: isoToday(),
      q: "",
      situacao: "TODOS",
      risco: "TODOS",
      periodoPreset: "MES",
    });
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 mb-8 card-shadow">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-heading-semibold text-lg text-card-foreground">
            Filtros e Pesquisa
          </h3>
          <p className="text-sm text-muted-foreground">
            Gestão de recebimentos e análise de inadimplência
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={isLoading}>
            <Icon name="RefreshCw" size={16} />
            <span className="ml-2">{isLoading ? "Atualizando..." : "Atualizar"}</span>
          </Button>

          {hasActive && (
            <Button variant="outline" size="sm" onClick={clear} disabled={isLoading}>
              <Icon name="X" size={16} />
              <span className="ml-2">Limpar</span>
            </Button>
          )}

          <Button variant="outline" size="sm" disabled>
            <Icon name="Download" size={16} />
            <span className="ml-2">Exportar</span>
          </Button>

          <Button variant="primary" size="sm" onClick={onNewCharge} disabled={isLoading}>
            <Icon name="Plus" size={16} />
            <span className="ml-2">Nova Cobrança</span>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end">
        <div className="lg:col-span-4">
          <Input
            value={local.q}
            onChange={(e) => emit({ ...local, q: e.target.value })}
            placeholder="Buscar cliente, e-mail ou telefone..."
          />
        </div>

        <div className="lg:col-span-2">
          <Select
            value={local.periodoPreset}
            onChange={(v) => setPreset(v)}
            options={periodoOptions}
            placeholder="Período"
          />
        </div>

        <div className="lg:col-span-2">
          <Select
            value={local.situacao}
            onChange={(v) => emit({ ...local, situacao: v })}
            options={statusOptions}
            placeholder="Status"
          />
        </div>

        <div className="lg:col-span-2">
          <Select
            value={local.risco}
            onChange={(v) => emit({ ...local, risco: v })}
            options={riscoOptions}
            placeholder="Risco"
          />
        </div>

        <div className="lg:col-span-2">
          <Select
            value={local.base}
            onChange={(v) => emit({ ...local, base: v })}
            options={baseOptions}
            placeholder="Empresa"
          />
        </div>

        {local.periodoPreset === "CUSTOM" && (
          <>
            <div className="lg:col-span-2">
              <label className="text-xs text-muted-foreground">De</label>
              <input
                type="date"
                value={local.de}
                onChange={(e) => emit({ ...local, de: e.target.value })}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </div>

            <div className="lg:col-span-2">
              <label className="text-xs text-muted-foreground">Até</label>
              <input
                type="date"
                value={local.ate}
                onChange={(e) => emit({ ...local, ate: e.target.value })}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReceivableFilterBar;
